// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import {
  spacing,
  fontSize,
  mediaMin,
  mediaMax,
} from 'presentations/views/styles';
import { black, gray04, primary } from 'presentations/views/styles/color';
import CalendarPageTemplate from 'presentations/views/components/templates/apps/CalendarPageTemplate';
import CalendarList from 'presentations/views/components/organisms/apps/CalendarList';
import HolidayList from 'presentations/views/components/organisms/apps/HolidayList';
import Headline from 'presentations/views/components/atoms/Headline';
import { CalendarArray } from 'usecases/app_calendar';
import { Gengo } from 'usecases/app_calendar/jp/gengo';
import { formatGengoYear } from 'usecases/app_calendar/jp/gengo/formatter';
import Breadcrumb from 'presentations/routes/breadcrumb';
import {
  appCalendarMonthFilterPath,
  appCalendarMonthPath,
  appCalendarPath,
  appCalendarTablePath,
  appCalendarYearFilterPath,
  appCalendarYearPath,
} from 'presentations/routes/path';
import FooterContentBanner from 'presentations/views/components/molecules/OwnedContentBanner/FooterContentBanner';

interface Props {
  calendars: CalendarArray[];
  calendarName: string;
  year: number;
  filter?: string;
  gengo: Gengo;
  holidays: any;
  holidayMasters: any;
  routes?: Breadcrumb[];
}

export default function Component(props: Props) {
  const router = useRouter();

  const t = translateInComponent(translations);
  const date = new Date(props.year, 1, 1);
  const gengo = t('gengoTitleKana', {
    year: formatGengoYear(props.year - props.gengo.start + 1),
    name: props.gengo.name,
    kana: props.gengo.kana,
  });

  return (
    <CalendarPageTemplate routes={props.routes}>
      <MainContents>
        <Content>
          <TitleHeadline variant="h1">
            {t('allYearlyCalendarTitle', {
              date,
              calendarName: props.calendarName,
            })}
          </TitleHeadline>
          <GengoHeadline>{gengo}</GengoHeadline>
          <ListContainer>
            <CalendarList
              year={props.year}
              calendars={props.calendars}
              filter={props.filter}
            />
          </ListContainer>
          {/* <StickyAdContainer>
            <StickyAdBanner productType={ProductType.Calendar} isHideClose />
          </StickyAdContainer> */}

          {props.holidays && (
            <HolidayListContainer>
              <SubHeadline variant="h2">
                {t('holidayTitle', { date })}
              </SubHeadline>
              <HolidayList
                holidays={props.holidays}
                holidayMasters={props.holidayMasters}
              />
            </HolidayListContainer>
          )}
          <ListContainer>
            <SubHeadline variant="h2">{t('searchTitle', { date })}</SubHeadline>
            <LinkListContainer>
              <LinkListInner>
                <LinkListTitle>{t('displayTypeTitle')}</LinkListTitle>
                <LinkListContent>
                  <LinkList>
                    <LinkListItem>
                      <LinkListItemLink
                        href={appCalendarYearPath(props.year, router.locale)}
                      >
                        {t('rokuyoCalendar')}
                      </LinkListItemLink>
                    </LinkListItem>
                    <LinkListItem>
                      <LinkListItemLink
                        href={appCalendarPath(
                          {
                            year: props.year,
                            display: 1,
                          },
                          router.locale,
                        )}
                      >
                        {t('moonAgeCalendar')}
                      </LinkListItemLink>
                    </LinkListItem>
                  </LinkList>
                </LinkListContent>
              </LinkListInner>
              <LinkListInner>
                <LinkListTitle>{t('filterTypeTitle')}</LinkListTitle>
                <LinkListContent>
                  <LinkList>
                    <LinkListItem>
                      <LinkListItemLink
                        href={appCalendarPath(
                          {
                            year: props.year,
                            filter: 'taian',
                          },
                          router.locale,
                        )}
                      >
                        {t('taianCalendar')}
                      </LinkListItemLink>
                    </LinkListItem>
                  </LinkList>
                </LinkListContent>
              </LinkListInner>
            </LinkListContainer>
          </ListContainer>

          <ListContainer>
            <SubHeadline variant="h2">
              {t('allCalendarLinkTitle', { year: props.year })}
            </SubHeadline>
            <LinkListContainer>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => {
                const y = props.year + i;
                if (y > 2030) return;
                const yearUrl = props.filter
                  ? appCalendarYearFilterPath(y, props.filter, router.locale)
                  : appCalendarYearPath(y, router.locale);
                return (
                  <LinkListInner key={y}>
                    <LinkListTitle>
                      <LinkListItemLink href={yearUrl}>
                        {t('date.year', {
                          date: new Date(y, 1, 1),
                        })}
                      </LinkListItemLink>
                    </LinkListTitle>
                    <LinkListContent>
                      <LinkList>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                          (month) => {
                            const monthUrl = props.filter
                              ? appCalendarMonthFilterPath(
                                  y,
                                  month,
                                  props.filter,
                                  router.locale,
                                )
                              : appCalendarMonthPath(y, month, router.locale);
                            return (
                              <LinkListItem key={month}>
                                <LinkListItemLink href={monthUrl}>
                                  {t('date.month', {
                                    date: new Date(y, month - 1, 1),
                                  })}
                                </LinkListItemLink>
                              </LinkListItem>
                            );
                          },
                        )}
                      </LinkList>
                    </LinkListContent>
                  </LinkListInner>
                );
              })}
            </LinkListContainer>
            <LinkListMore>
              <LinkListItemLink href={appCalendarTablePath(router.locale)}>
                {t('allCalendarLink')}
              </LinkListItemLink>
            </LinkListMore>
          </ListContainer>
        </Content>
      </MainContents>
    </CalendarPageTemplate>
  );
}

const LinkListMore = styled.div`
  margin-top: ${spacing(12)};
  ${mediaMin.tablet} {
    margin-top: ${spacing(16)};
  }
`;

const HolidayListContainer = styled.div`
  margin-bottom: ${spacing(48)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(32)};
  }
`;
const LinkListContainer = styled.dl`
  ${mediaMin.tablet} {
    padding: ${spacing(24)} 0 ${spacing(12)};
  }
`;
const LinkListTitle = styled.dt`
  font-size: ${fontSize(14)};
  flex-shrink: 0;
  padding-top: ${spacing(16)};
  ${mediaMin.tablet} {
    font-size: ${fontSize(16)};
    padding-top: 0;
    padding-right: 1em;
  }
`;
const LinkListInner = styled.div`
  border-top: 1px solid ${gray04};
  ${mediaMin.tablet} {
    border-top: 0;
    font-size: ${fontSize(16)};
    display: flex;
    :not(:first-of-type) {
      margin-top: ${fontSize(12)};
    }
  }
`;
const LinkListContent = styled.dd`
  width: 100%;
  padding: ${spacing(16)} 0 0;
  ${mediaMin.tablet} {
    padding: 0;
  }
`;
const LinkList = styled.ul`
  display: flex;
  flex-wrap: wrap;
`;
const LinkListItem = styled.li`
  font-size: ${fontSize(14)};
  padding-right: ${spacing(24)};
  margin-bottom: ${spacing(16)};
  ${mediaMin.tablet} {
    font-size: ${fontSize(16)};
    margin-bottom: ${spacing(12)};
  }
`;
const LinkListItemLink = styled.a`
  color: ${primary};
  ${mediaMax.tablet} {
    display: block;
  }
  &:hover {
    color: ${primary};
    text-decoration: underline;
  }
`;

const MainContents = styled.main`
  padding: 0 ${spacing(16)};
  ${mediaMax.tablet} {
    padding: 0;
  }
`;
const Content = styled.div`
  color: ${black};
  ${mediaMin.tablet} {
    max-width: 1100px;
    margin: 0 auto;
  }
`;
const TitleHeadline = styled(Headline)`
  text-align: center;
  margin-top: ${spacing(24)};
  ${mediaMin.tablet} {
    font-size: ${fontSize(32)};
  }
`;
const GengoHeadline = styled.div`
  margin-bottom: ${spacing(24)};
  text-align: center;
`;
const SubHeadline = styled(Headline)`
  font-size: ${fontSize(32)};
  text-align: center;
  margin-bottom: ${spacing(16)};
  ${mediaMin.tablet} {
    font-size: ${fontSize(32)};
  }
`;

const ListContainer = styled.div`
  margin-bottom: ${spacing(48)};
  padding: ${spacing(16)} 0;
  ${mediaMax.tablet} {
    padding: ${spacing(16)};
    margin-bottom: ${spacing(32)};
  }
`;

const NavigationContainer = styled.div`
  margin-bottom: ${spacing(32)};
  position: sticky;
  border-bottom: 1px solid ${gray04};
`;
const StickyAdContainer = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  ${mediaMin.tablet} {
    display: none;
  }
`;
const StickyAdBanner = styled(FooterContentBanner)`
  padding: ${spacing(12)} ${spacing(12)};
  ${mediaMax.tablet} {
    height: auto;
  }
`;
